/**
 * FileUpload
 * Drag-and-drop file upload component with type validation,
 * max size (5MB), image preview, progress placeholder, and remove button.
 */
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  pdf: ['application/pdf'],
};

export default function FileUpload({
  label,
  accept,
  file,
  onChange,
  required,
  error,
  helperText,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const acceptedMimes = accept === 'image'
    ? ACCEPTED_TYPES.image
    : accept === 'pdf'
      ? ACCEPTED_TYPES.pdf
      : [...ACCEPTED_TYPES.image, ...ACCEPTED_TYPES.pdf];

  const acceptString = accept === 'image'
    ? 'image/*'
    : accept === 'pdf'
      ? '.pdf'
      : 'image/*,.pdf';

  const validateFile = useCallback((f) => {
    if (!acceptedMimes.includes(f.type)) {
      return `Invalid file type. Accepted: ${accept === 'image' ? 'Images' : accept === 'pdf' ? 'PDF' : 'Images or PDF'}`;
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB`;
    }
    return null;
  }, [acceptedMimes, accept]);

  const handleFile = useCallback((f) => {
    setLocalError(null);
    const err = validateFile(f);
    if (err) {
      setLocalError(err);
      return;
    }
    // Simulate upload progress
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30 + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setTimeout(() => setUploadProgress(null), 500);
      }
      setUploadProgress(Math.min(progress, 100));
    }, 200);

    onChange(f);
  }, [validateFile, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const f = e.target.files[0];
    if (f) handleFile(f);
    // Reset input so same file can be re-selected
    e.target.value = '';
  }, [handleFile]);

  const handleRemove = useCallback(() => {
    onChange(null);
    setLocalError(null);
    setUploadProgress(null);
  }, [onChange]);

  const isImage = file && file.type && file.type.startsWith('image/');
  const displayError = error || localError;

  return (
    <div>
      {label && (
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
        </label>
      )}

      {!file ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            padding: '24px',
            borderRadius: '10px',
            border: `2px dashed ${dragOver ? '#3b82f6' : displayError ? '#ef4444' : '#d1d5db'}`,
            backgroundColor: dragOver ? '#f0f9ff' : '#f8fafc',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={acceptString}
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
          <div style={{ marginBottom: '8px' }}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" style={{ margin: '0 auto' }}>
              <path d="M16 4L16 20M16 4L10 10M16 4L22 10" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 22L4 26C4 27.1046 4.89543 28 6 28L26 28C27.1046 28 28 27.1046 28 26L28 22" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p style={{ fontSize: '14px', color: '#475569', margin: '0 0 4px', fontWeight: '500' }}>
            Drag and drop or click to upload
          </p>
          <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
            {accept === 'image' ? 'JPG, PNG, GIF, WebP, SVG' : accept === 'pdf' ? 'PDF' : 'Images or PDF'} up to 5MB
          </p>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#fff',
        }}>
          {/* Thumbnail for images */}
          {isImage ? (
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '6px',
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: '#f1f5f9',
            }}>
              <img
                src={URL.createObjectURL(file)}
                alt="Preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ) : (
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '6px',
              backgroundColor: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 2V8H20" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}

          {/* File info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
              {(file.size / 1024).toFixed(1)} KB
            </div>
            {/* Progress bar placeholder */}
            {uploadProgress !== null && uploadProgress < 100 && (
              <div style={{ marginTop: '4px', height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${uploadProgress}%`,
                  backgroundColor: '#3b82f6',
                  borderRadius: '2px',
                  transition: 'width 0.2s ease',
                }} />
              </div>
            )}
          </div>

          {/* Remove button */}
          <button
            type="button"
            onClick={handleRemove}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#ef4444',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Remove
          </button>
        </div>
      )}

      {displayError && <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{displayError}</div>}
      {helperText && !displayError && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{helperText}</div>}
    </div>
  );
}

FileUpload.propTypes = {
  label: PropTypes.string,
  accept: PropTypes.oneOf(['image', 'pdf', 'all']),
  file: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
};

FileUpload.defaultProps = {
  accept: 'all',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};
